import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc, 
  doc, 
  Timestamp, 
  QueryConstraint,
  getCountFromServer,
  startAfter,
  DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { startOfDay, endOfDay } from 'date-fns';

export interface FollowUpItem {
  id: string;
  followUp: string;
  followUpTime: Timestamp;
  isDone: boolean;
  leadId: string;
  studentName: string;
  studentNumber: string;
  userId: string;
  assignedUserName: string;
  leadStage: string;
  status: 'upcoming' | 'today' | 'overdue';
}

export interface FollowUpMetrics {
  totalFollowUps: number;
  upcomingFollowUps: number;
  todaysFollowUps: number;
  overdueFollowUps: number;
}

export interface FollowUpFilters {
  companyId: string;
  userIds?: string[];
  startTime?: string | null;
  endTime?: string | null;
  currentScreen: string;
  isAdmin: boolean;
  isManager: boolean;
  isStaff: boolean;
  currentUserId?: string; // Add current user ID for fallback scenarios
}

// Helper function to process follow-up data with user information
const processFollowUpData = async (docSnapshot: any, companyId: string): Promise<FollowUpItem> => {
  const data = docSnapshot.data();
  const leadDocRef = doc(db, `companies/${companyId}/leads`, data.leadId);
  const leadDoc = await getDoc(leadDocRef);
  const leadData = leadDoc.data();
  const studentName = leadData?.name || 'Unknown Student';
  const studentNumber = leadData?.number || 'No Number';

  // Fetch user name from users collection
  let assignedUserName = 'Unknown User';
  if (data.userId) {
    const userDocRef = doc(db, 'users', data.userId);
    const userDoc = await getDoc(userDocRef);
    if (userDoc.exists()) {
      assignedUserName = userDoc.data()?.name || 'Unknown User';
    }
  }

  // Fetch lead stage
  let leadStage = 'No Stage';
  console.log("lead data", leadData);
  console.log("Available lead data keys:", leadData ? Object.keys(leadData) : 'No leadData');
  
  // Check for different possible stage field names
  const stageValue = leadData?.stageId;

  console.log(`Processing follow-up ${docSnapshot.id} for lead ${data.leadId} with stage ${stageValue}`);
  

  if (stageValue) {
    try {
      const stageDocRef = doc(db, `companies/${companyId}/leadStages`, stageValue);
      const stageDoc = await getDoc(stageDocRef);
      if (stageDoc.exists()) {
        leadStage = stageDoc.data()?.stage || 'Unknown Stage';
      }
    } catch (error) {
      console.error('Error fetching stage:', error);
      leadStage = 'Unknown Stage';
    }
  }

  // Determine status based on follow-up time
  const followUpDate = data.followUpTime.toDate();
  const now = new Date(); // Current date and time
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  
  let status: 'upcoming' | 'today' | 'overdue' = 'upcoming';
  
  if (followUpDate < todayStart) {
    // Only follow-ups before today (excluding today) are overdue
    status = 'overdue';
  } else if (followUpDate >= todayStart && followUpDate <= todayEnd) {
    // All follow-ups scheduled for today (regardless of current time)
    status = 'today';
  } else {
    // Future dates (after today)
    status = 'upcoming';
  }

  return {
    id: docSnapshot.id,
    followUp: data.followUp,
    followUpTime: data.followUpTime,
    isDone: data.isDone,
    leadId: data.leadId,
    studentName,
    studentNumber,
    userId: data.userId,
    assignedUserName,
    leadStage,
    status
  };
};

// Fetch follow-up metrics (counts)
export const fetchFollowUpMetrics = async (filters: FollowUpFilters): Promise<FollowUpMetrics> => {
  try {
    const followUpsRef = collection(db, `companies/${filters.companyId}/followUps`);
    
    // Base conditions
    let baseConditions: QueryConstraint[] = [
      where('isDone', '==', false)
    ];

    // Apply user filters based on role and selection
    if (filters.userIds && filters.userIds.length > 0) {
      if (filters.userIds.length === 1) {
        baseConditions.push(where('userId', '==', filters.userIds[0]));
      } else if (filters.userIds.length <= 10) {
        baseConditions.push(where('userId', 'in', filters.userIds));
      }
      // For more than 10 users, we'll filter in memory
    } else {
      // Apply role-based filtering when no specific users selected
      if (filters.isManager && filters.currentScreen === 'Manager' && filters.currentUserId) {
        // Manager sees follow-ups from users under their management
        const usersQuery = query(
          collection(db, 'users'),
          where('companyId', '==', filters.companyId),
          where("managerIds", "array-contains", filters.currentUserId)
        );
        const usersSnapshot = await getDocs(usersQuery);
        const userIds = usersSnapshot.docs.map(doc => doc.id);
        
        if (userIds.length > 0) {
          if (userIds.length <= 10) {
            baseConditions.push(where('userId', 'in', userIds));
          }
          // For more than 10 users, we'll filter in memory
        } else {
          // If no users under manager, return zero counts
          return {
            totalFollowUps: 0,
            upcomingFollowUps: 0,
            todaysFollowUps: 0,
            overdueFollowUps: 0
          };
        }
      } else if (filters.isStaff && filters.currentScreen === 'Staff' && filters.currentUserId) {
        // Staff sees only their own follow-ups
        baseConditions.push(where('userId', '==', filters.currentUserId));
      }
      // For Admin, show all follow-ups for the company (no additional filter needed)
    }

    const now = new Date(); // Current date and time
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);

    // Get today's follow-ups (from start of day to end of day) - matching FollowUps.tsx logic
    const todayQuery = query(
      followUpsRef,
      ...baseConditions,
      where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
      where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
    );
    const todaySnapshot = await getCountFromServer(todayQuery);

    // Get upcoming follow-ups (future dates)
    const upcomingQuery = query(
      followUpsRef,
      ...baseConditions,
      where('followUpTime', '>', Timestamp.fromDate(todayEnd))
    );
    const upcomingSnapshot = await getCountFromServer(upcomingQuery);

    // Get overdue follow-ups (before today, excluding today's follow-ups to prevent duplication)
    const overdueQuery = query(
      followUpsRef,
      ...baseConditions,
      where('followUpTime', '<', Timestamp.fromDate(todayStart))
    );
    const overdueSnapshot = await getCountFromServer(overdueQuery);

    const todaysFollowUps = todaySnapshot.data().count;
    const upcomingFollowUps = upcomingSnapshot.data().count;
    const overdueFollowUps = overdueSnapshot.data().count;
    const totalFollowUps = todaysFollowUps + upcomingFollowUps + overdueFollowUps;

    return {
      totalFollowUps,
      upcomingFollowUps,
      todaysFollowUps,
      overdueFollowUps
    };
  } catch (error) {
    console.error('Error fetching follow-up metrics:', error);
    throw error;
  }
};

// Fetch detailed follow-up items for a specific type
export const fetchFollowUpItems = async (
  filters: FollowUpFilters, 
  type: 'upcoming' | 'today' | 'overdue',
  limitCount: number = 1000,
  lastDoc?: DocumentSnapshot
): Promise<{items: FollowUpItem[], lastDocument: DocumentSnapshot | null, hasMore: boolean}> => {
  try {
    const followUpsRef = collection(db, `companies/${filters.companyId}/followUps`);
    
    // Base conditions
    let conditions: QueryConstraint[] = [
      where('isDone', '==', false),
      orderBy('followUpTime', 'asc'),
      limit(limitCount)
    ];

    // Add pagination if lastDoc is provided
    if (lastDoc) {
      conditions.push(startAfter(lastDoc));
    }

    // Apply time-based filter conditions
    const now = new Date(); // Current date and time
    const todayStart = startOfDay(now);
    const todayEnd = endOfDay(now);

    if (type === 'today') {
      // All follow-ups scheduled for today (from start of day to end of day)
      conditions.push(
        where('followUpTime', '>=', Timestamp.fromDate(todayStart)),
        where('followUpTime', '<=', Timestamp.fromDate(todayEnd))
      );
    } else if (type === 'upcoming') {
      conditions.push(where('followUpTime', '>', Timestamp.fromDate(todayEnd)));
    } else if (type === 'overdue') {
      // Overdue follow-ups (before today, excluding today's follow-ups to prevent duplication)
      conditions.push(where('followUpTime', '<', Timestamp.fromDate(todayStart)));
    }

    // Apply user filters based on role and selection
    if (filters.userIds && filters.userIds.length > 0) {
      if (filters.userIds.length === 1) {
        conditions.push(where('userId', '==', filters.userIds[0]));
      } else if (filters.userIds.length <= 10) {
        conditions.push(where('userId', 'in', filters.userIds));
      }
      // For more than 10 users, we'll filter in memory
    } else {
      // Apply role-based filtering when no specific users selected
      if (filters.isStaff && filters.currentScreen === 'Staff' && filters.currentUserId) {
        // Staff sees only their own follow-ups
        conditions.push(where('userId', '==', filters.currentUserId));
      }
      // For Manager and Admin screens, if no users are selected, show all follow-ups for the company
      // (no additional filter needed as we already filter by company)
    }

    console.log(`Fetching ${type} follow-ups with ${conditions.length} conditions`);

    const followUpsQuery = query(followUpsRef, ...conditions);
    const snapshot = await getDocs(followUpsQuery);

    // Check if there are more items available
    const hasMore = snapshot.docs.length === limitCount;
    const lastDocument = snapshot.docs.length > 0 ? snapshot.docs[snapshot.docs.length - 1] : null;

    // Process follow-up data
    const followUpsData = await Promise.all(
      snapshot.docs.map(docSnapshot => processFollowUpData(docSnapshot, filters.companyId))
    );

    // Filter in memory if we have more than 10 users (Firebase 'in' query limitation)
    let filteredData = followUpsData;
    if (filters.userIds && filters.userIds.length > 10) {
      filteredData = followUpsData.filter(item => filters.userIds!.includes(item.userId));
    }

    console.log(`Fetched ${filteredData.length} ${type} follow-ups`);
    
    return {
      items: filteredData,
      lastDocument,
      hasMore
    };
  } catch (error) {
    console.error(`Error fetching ${type} follow-up items:`, error);
    throw error;
  }
};
